import { Pill } from '../ui/pill';
import { Separator } from '../ui/separator';
import { cn } from '../ui/utils';
import { Bluetooth, Zap, Activity, HardDrive, Brain, HeartPulse, Move3d, Battery, Database, Cpu } from 'lucide-react';
import { useSystemStatus, useDeviceStatus, useStreamingStatus, useRecordingStatus, useSensorContactStatus } from '../../stores/systemStore';
import { useStorageStore } from '../../stores/storageStore';
import { useProcessedDataStore } from '../../stores/processedDataStore';
import { useRawDataStore } from '../../stores/rawDataStore';
import { useDeviceStore } from '../../stores/deviceStore';
import { bluetoothService } from '../../utils/bluetoothService';
import { useState, useEffect } from 'react';
import { useTranslation } from '../../locales';
import { useLanguageStore } from '../../stores/languageStore';
import { useUIStore } from '../../stores/uiStore';

export function StatusBar() {
  const { currentLanguage } = useLanguageStore();
  const t = useTranslation(currentLanguage);
  const { activeMenu, setActiveMenu } = useUIStore();
  const { isInitialized } = useSystemStatus();
  const { isConnected } = useDeviceStatus();
  const { isStreaming } = useStreamingStatus();
  const { isRecording } = useRecordingStatus();
  const { isSensorContacted, leadOffStatus } = useSensorContactStatus();
  
  // 저장소 상태
  const { storageStats, currentSession } = useStorageStore(); // SDD 010: storageStatus 제거
  
  // PPG 프로세싱 데이터 버퍼 카운트
  const ppgAnalysis = useProcessedDataStore(state => state.ppgAnalysis);
  const dataStats = useRawDataStore(state => state.dataStats);
  
  // DeviceStore에서 샘플링 레이트 가져오기
  const connectedDevice = useDeviceStore(state => state.connectedDevice);
  const samplingRates = connectedDevice?.samplingRates || null;
  
  // 실시간 배터리 정보
  const [realtimeBatteryLevel, setRealtimeBatteryLevel] = useState<number>(0);
  
  // 실시간 레코딩 시간 계산
  const [recordingDuration, setRecordingDuration] = useState<string>('00:00:00');
  
  // 실시간 데이터 크기
  const [realTimeDataSize, setRealTimeDataSize] = useState<number>(0);
  
  useEffect(() => {
    let interval: NodeJS.Timeout;
    
    if (isRecording && currentSession?.startTime) {
      interval = setInterval(() => {
        const now = Date.now();
        const start = new Date(currentSession.startTime).getTime();
        const diff = Math.floor((now - start) / 1000);
        
        const hours = Math.floor(diff / 3600);
        const minutes = Math.floor((diff % 3600) / 60);
        const seconds = diff % 60;
        
        setRecordingDuration(
          `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
        );
      }, 1000);
    } else {
      setRecordingDuration('00:00:00');
    }
    
    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [isRecording, currentSession?.startTime]);

  // 배터리 레벨 업데이트
  useEffect(() => {
    let interval: NodeJS.Timeout;
    
    if (isConnected && bluetoothService.isConnected()) {
      interval = setInterval(async () => {
        try {
          const batteryLevel = await bluetoothService.getBatteryLevel();
          setRealtimeBatteryLevel(batteryLevel);
        } catch (error) {
          console.error('Failed to get realtime battery level:', error);
        }
      }, 1000); // 1초마다 업데이트
    } else {
      setRealtimeBatteryLevel(0);
    }
    
    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [isConnected]);

  // 실시간 데이터 크기 업데이트
  useEffect(() => {
    let interval: NodeJS.Timeout;
    
    if (isRecording && currentSession) {
      interval = setInterval(async () => {
        try {
          // S3 클라우드 저장 모드인지 확인
          // IndexedDB 기반 — currentSession에서 직접 데이터 크기 사용
          setRealTimeDataSize(currentSession.estimatedSize || 0);
        } catch (error) {
          console.error('실시간 데이터 크기 업데이트 실패:', error);
          setRealTimeDataSize(currentSession.estimatedSize || 0);
        }
      }, 1000); // 1초마다 업데이트
    } else {
      setRealTimeDataSize(0);
    }
    
    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [isRecording, currentSession]);

  // 포맷팅 함수들
  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
  };

  // 실시간 데이터 크기 가져오기
  const getRealTimeDataSize = (): number => {
    return realTimeDataSize;
  };

  // Pill 색상 클래스
  const GOOD_CLASS = 'bg-green-500/10 text-green-400 border-green-500/20';
  const MUTED_CLASS = 'bg-muted text-muted-foreground';
  const REC_CLASS = 'bg-red-500/10 text-red-400 border-red-500/20';

  // 1) Device 상태 로직
  const getDeviceStatus = () => {
    if (isConnected) {
      return { text: t.statusbar.connected, className: GOOD_CLASS };
    }
    return { text: t.statusbar.disconnected, className: MUTED_CLASS };
  };

  // 2) Sensor 상태 로직
  const getSensorStatus = () => {
    if (!isConnected) return { text: t.statusbar.disconnected, className: MUTED_CLASS };
    if (isSensorContacted) return { text: t.statusbar.goodContact, className: GOOD_CLASS };
    return { text: t.statusbar.poorContact, className: REC_CLASS };
  };

  // 3) Streaming 상태 로직
  const getStreamingStatus = () => {
    if (!isConnected) return { text: t.statusbar.disconnected, className: MUTED_CLASS };
    if (isStreaming) return { text: t.statusbar.streaming, className: GOOD_CLASS };
    return { text: t.statusbar.stopped, className: MUTED_CLASS };
  };

  // 4) Recording 상태 로직
  const getRecordingStatus = () => {
    if (!isConnected) return { text: t.statusbar.disconnected, className: MUTED_CLASS };
    if (isRecording) {
      return {
        text: `${recordingDuration} • ${formatFileSize(getRealTimeDataSize())}`,
        className: REC_CLASS,
      };
    }
    return { text: t.statusbar.stopped, className: MUTED_CLASS };
  };

  // 각 상태 객체 생성
  const deviceStatus = getDeviceStatus();
  const sensorStatus = getSensorStatus();
  const streamingStatus = getStreamingStatus();
  const recordingStatus = getRecordingStatus();

  return (
    <footer className="status-bar bg-white/[0.02] backdrop-blur-md border-t border-white/[0.06] px-8 py-3">
      {/* ── Desktop (≥ 1024px): 전체 텍스트 + Pill ── */}
      <div className="hidden lg:flex items-center justify-between text-sm w-full">

        {/* 왼쪽: 핵심 4가지 상태 */}
        <div className="flex items-center space-x-8">

          {/* 1) Device 상태 */}
          <div className="flex items-center gap-2">
            <Bluetooth strokeWidth={1.5} className="h-4 w-4 text-indigo-400" />
            <span className="text-xs text-muted-foreground">{t.statusbar.connection}:</span>
            <Pill size="sm" variant="secondary" className={cn("pointer-events-none", deviceStatus.className)}>
              {deviceStatus.text}
            </Pill>
          </div>

          <Separator orientation="vertical" className="h-6" />

          {/* 2) Sensor 상태 */}
          <div className="flex items-center gap-2">
            <Zap strokeWidth={1.5} className="h-4 w-4 text-yellow-500" />
            <span className="text-xs text-muted-foreground">{t.statusbar.sensors}:</span>
            <Pill size="sm" variant="secondary" className={cn("pointer-events-none", sensorStatus.className)}>
              {sensorStatus.text}
            </Pill>
            {isConnected && !isSensorContacted && (
              <span className="text-xs text-muted-foreground">
                (FP1: {leadOffStatus.fp1 ? 'OFF' : 'ON'}, FP2: {leadOffStatus.fp2 ? 'OFF' : 'ON'})
              </span>
            )}
          </div>

          <Separator orientation="vertical" className="h-6" />

          {/* 3) Streaming 상태 */}
          <div className="flex items-center gap-2">
            <Activity strokeWidth={1.5} className="h-4 w-4 text-green-500" />
            <span className="text-xs text-muted-foreground">{t.statusbar.streaming}:</span>
            <Pill size="sm" variant="secondary" className={cn("pointer-events-none", streamingStatus.className)}>
              {streamingStatus.text}
            </Pill>
          </div>

          <Separator orientation="vertical" className="h-6" />

          {/* 4) Recording 상태 */}
          <div className="flex items-center gap-2">
            <HardDrive strokeWidth={1.5} className="h-4 w-4 text-red-500" />
            <span className="text-xs text-muted-foreground">{t.statusbar.recording}:</span>
            <Pill size="sm" variant="secondary" className={cn("pointer-events-none", recordingStatus.className)}>
              {recordingStatus.text}
            </Pill>
          </div>
        </div>

        {/* 오른쪽: 샘플링 레이트 및 배터리 */}
        <div className="flex items-center space-x-6">
          <div className="hidden 2xl:flex items-center gap-2 min-w-fit">
            <Brain strokeWidth={1.5} className="h-4 w-4 text-chart-1" />
            <span className="text-xs text-foreground">EEG: {isConnected && samplingRates?.eeg ? samplingRates.eeg.toFixed(1) : '-'} Hz</span>
          </div>

          <div className="hidden 2xl:flex items-center gap-2 min-w-fit">
            <HeartPulse strokeWidth={1.5} className="h-4 w-4 text-chart-2" />
            <span className="text-xs text-foreground">PPG: {isConnected && samplingRates?.ppg ? samplingRates.ppg.toFixed(1) : '-'} Hz</span>
          </div>

          <div className="hidden 2xl:flex items-center gap-2 min-w-fit">
            <Move3d strokeWidth={1.5} className="h-4 w-4 text-chart-3" />
            <span className="text-xs text-foreground">ACC: {isConnected && samplingRates?.acc ? samplingRates.acc.toFixed(1) : '-'} Hz</span>
          </div>

          <div className="flex items-center gap-2 min-w-fit">
            <Battery strokeWidth={1.5} className="h-4 w-4 text-chart-4" />
            <span className="text-xs text-foreground">{t.statusbar.battery}: {isConnected && realtimeBatteryLevel > 0 ? `${realtimeBatteryLevel}%` : '-'}</span>
          </div>
        </div>

      </div>

      {/* ── Mobile + Tablet (< 1024px): 하단 네비게이션 바 ── */}
      <nav className="flex lg:hidden items-stretch w-full" role="navigation" aria-label="하단 네비게이션">
        {([
          { id: 'linkband' as const,       Icon: Bluetooth, label: t.sidebar.deviceManager,
            active: isConnected,   dotColor: isConnected ? 'bg-green-400 shadow-[0_0_6px_rgba(74,222,128,0.5)]' : 'bg-slate-600',
            iconColor: isConnected ? 'text-green-400' : 'text-slate-500' },
          { id: 'visualizer' as const,     Icon: Activity,   label: t.sidebar.visualizer,
            active: isConnected && isStreaming, dotColor: isConnected && isStreaming ? 'bg-green-400 animate-pulse shadow-[0_0_6px_rgba(74,222,128,0.5)]' : 'bg-slate-600',
            iconColor: isConnected && isStreaming ? 'text-green-400' : 'text-slate-500' },
          { id: 'datacenter' as const,     Icon: Database,   label: t.sidebar.dataCenter,
            active: isRecording, dotColor: isRecording ? 'bg-red-400 animate-pulse shadow-[0_0_6px_rgba(248,113,113,0.5)]' : 'bg-slate-600',
            iconColor: isRecording ? 'text-red-400' : 'text-slate-500' },
          { id: 'stream-process' as const, Icon: Cpu,        label: t.sidebar.streamProcess,
            active: isConnected,   dotColor: isConnected ? 'bg-indigo-400 shadow-[0_0_6px_rgba(129,140,248,0.5)]' : 'bg-slate-600',
            iconColor: isConnected ? 'text-indigo-400' : 'text-slate-500' },
        ] as const).map(({ id, Icon, label, dotColor, iconColor }) => {
          const isActive = activeMenu === id;
          return (
            <button
              key={id}
              type="button"
              onClick={() => setActiveMenu(id)}
              className={cn(
                "flex-1 flex flex-col items-center justify-center gap-0.5 py-1.5 transition-colors",
                "active:bg-white/[0.04] focus:outline-none",
                isActive ? "text-indigo-400" : "text-slate-500"
              )}
              aria-label={label}
              aria-current={isActive ? 'page' : undefined}
            >
              <Icon strokeWidth={isActive ? 2 : 1.5} className={cn("h-5 w-5 transition-all", isActive ? iconColor : '')} />
              <span className="text-[9px] leading-none font-medium truncate max-w-full px-0.5">
                {label}
              </span>
              <span className={cn("w-1.5 h-1.5 rounded-full transition-colors", dotColor)} />
            </button>
          );
        })}
      </nav>
    </footer>
  );
} 